import { useState, useEffect } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { groupFieldsByCategory } from "./collect-fields";

interface FieldFilterModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  availableFields: Set<string>;
  selectedFields: Set<string>;
  onApply: (selectedFields: Set<string>) => void;
}

/**
 * Format field name from camelCase/path to readable format
 */
function formatFieldName(fieldPath: string): string {
  // Remove array indices and normalize wildcards for display
  // Replace [0], [1], etc. with nothing, and .* with nothing (but keep the structure)
  let withoutIndices = fieldPath.replace(/\[\d+\]/g, "");
  // Remove .* wildcards (they represent "any index" but don't need to be shown)
  withoutIndices = withoutIndices.replace(/\.\*/g, "");
  // Split by dots and format each part
  return withoutIndices
    .split(".")
    .filter(part => part.length > 0) // Filter out empty parts from removed wildcards
    .map(part => {
      // Convert camelCase to Title Case
      return part
        .replace(/([A-Z])/g, " $1")
        .replace(/^./, (str) => str.toUpperCase())
        .trim();
    })
    .join(" → ");
}

export function FieldFilterModal({
  open,
  onOpenChange,
  availableFields,
  selectedFields,
  onApply,
}: FieldFilterModalProps) {
  const [localSelected, setLocalSelected] = useState<Set<string>>(new Set(selectedFields));

  // Update local state when selectedFields prop changes
  useEffect(() => {
    setLocalSelected(new Set(selectedFields));
  }, [selectedFields, open]);

  const grouped = groupFieldsByCategory(availableFields);
  const categories = [
    { key: "unit", label: "Unit Fields", fields: grouped.unit },
    { key: "weapon", label: "Weapon Fields", fields: grouped.weapon },
    { key: "ammo", label: "Ammo Fields", fields: grouped.ammo },
    { key: "other", label: "Other Fields", fields: grouped.other },
  ].filter(cat => cat.fields.length > 0);

  const toggleField = (field: string) => {
    const newSelected = new Set(localSelected);
    if (newSelected.has(field)) {
      newSelected.delete(field);
    } else {
      newSelected.add(field);
    }
    setLocalSelected(newSelected);
  };


  const selectAllInCategory = (fields: string[]) => {
    const newSelected = new Set(localSelected);
    fields.forEach(field => newSelected.add(field));
    setLocalSelected(newSelected);
  };

  const deselectAllInCategory = (fields: string[]) => {
    const newSelected = new Set(localSelected);
    fields.forEach(field => newSelected.delete(field));
    setLocalSelected(newSelected);
  };

  const handleApply = () => {
    onApply(localSelected);
    onOpenChange(false);
  };

  const handleReset = () => {
    setLocalSelected(new Set());
  };

  const handleCancel = () => {
    setLocalSelected(new Set(selectedFields)); // Reset to original
    onOpenChange(false);
  };

  const allFieldsCount = availableFields.size;
  const selectedCount = localSelected.size;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[80vh] overflow-hidden flex flex-col sm:max-w-4xl lg:max-w-6xl">
        <DialogHeader>
          <DialogTitle>Filter Fields</DialogTitle>
          <DialogDescription>
            Select which fields you want to see in the patch notes. Changes to unselected fields will be hidden.
            {selectedCount > 0 && (
              <span className="block mt-1 text-sm">
                {selectedCount} of {allFieldsCount} fields selected
              </span>
            )}
          </DialogDescription>
        </DialogHeader>
        
        <div className="flex-1 overflow-y-auto pr-2">
          <Accordion type="multiple" defaultValue={categories.map(c => c.key)} className="space-y-2">
            {categories.map((category) => {
              const categorySelectedCount = category.fields.filter(f => localSelected.has(f)).length;
              const allSelected = categorySelectedCount === category.fields.length;
              const someSelected = categorySelectedCount > 0 && categorySelectedCount < category.fields.length;

              return (
                <AccordionItem key={category.key} value={category.key} className="border rounded-lg px-2">
                  <div className="flex items-center justify-between">
                    <AccordionTrigger className="flex-1 py-2 hover:no-underline">
                      <div className="flex items-center gap-2">
                        <span className="font-medium">
                          {category.label} ({category.fields.length})
                        </span>
                        {someSelected && (
                          <span className="text-xs text-muted-foreground">
                            ({categorySelectedCount} selected)
                          </span>
                        )}
                      </div>
                    </AccordionTrigger>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-6 px-2 text-xs mr-2"
                      onClick={(e) => {
                        e.stopPropagation();
                        if (allSelected) {
                          deselectAllInCategory(category.fields);
                        } else {
                          selectAllInCategory(category.fields);
                        }
                      }}
                    >
                      {allSelected ? "Deselect All" : "Select All"}
                    </Button>
                  </div>
                  <AccordionContent>
                    <div className="px-2 pb-2 space-y-2 max-h-60 overflow-y-auto">
                      {category.fields.map((field) => {
                        const isSelected = localSelected.has(field);
                        return (
                          <div key={field} className="flex items-center space-x-2">
                            <Checkbox
                              id={field}
                              checked={isSelected}
                              onCheckedChange={() => toggleField(field)}
                            />
                            <Label
                              htmlFor={field}
                              className="text-sm font-normal cursor-pointer flex-1"
                            >
                              {formatFieldName(field)}
                            </Label>
                            <span className="text-xs text-muted-foreground font-mono">
                              {field}
                            </span>
                          </div>
                        );
                      })}
                    </div>
                  </AccordionContent>
                </AccordionItem>
              );
            })}
          </Accordion>
        </div>

        <DialogFooter className="flex-shrink-0">
          <Button variant="outline" onClick={handleReset}>
            Reset
          </Button>
          <Button variant="outline" onClick={handleCancel}>
            Cancel
          </Button>
          <Button onClick={handleApply}>
            Apply ({selectedCount > 0 ? selectedCount : "All"})
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

